import React from "react";
import {
  Box,
  Container,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import PageTitle from "../../widgets/PageTitle"; // Assuming you have this component
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";

const CampaignApiDoc = () => {
  const {
    data: apiCred,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(`/user/get-api-cred`);

  if (isLoading) {
    return <MuPb />;
  }
  // Sample request body
  const requestBody = `{
  "templateId": "7667676877878",
  "apiCode": "ere323",
  "campaignName": "fefreR434",
  "filename": "Sample.pdf",
  "phoneNumbers": ["914567892345", "915678092314"],
  "mediaUrl": "https://i.ibb.co/y6FcDJK/whatsapp-2022-0-sixteen-nine.jpg",
  "bodyParams": ["Parameter1", "Parameter2"],
  "headerParams": ["Parameter1"]
}`;

  // Parameter descriptions
  const parameters = [
    {
      name: "templateId",
      type: "string",
      required: true,
      description: "The ID of the WhatsApp template to use for the campaign.",
    },
    {
      name: "apiCode",
      type: "string",
      required: true,
      description:
        "Unique API code associated with your account for authentication.",
    },
    {
      name: "campaignName",
      type: "string",
      required: true,
      description: "A unique name to identify the campaign.",
    },
    {
      name: "phoneNumbers",
      type: "array of strings",
      required: true,
      description:
        "List of phone numbers to send the campaign to (in E.164 format, e.g., +1234567890).",
    },
    {
      name: "mediaUrl",
      type: "string",
      required: false,
      description:
        "URL of the media file to include in the message (e.g., image, video). Must be publicly accessible.",
    },
    {
      name: "bodyParams",
      type: "array of strings",
      required: false,
      description:
        "Array of parameters to replace placeholders in the template body.",
    },
    {
      name: "headerParams",
      type: "array of strings",
      required: false,
      description:
        "Array of parameters to replace placeholders in the template header.",
    },
    {
      name: "filename",
      type: "string",
      required: false,
      description: "pass if u pdf document and you want to show the filename",
    },
  ];

  return (
    <Container sx={{ mt: 4, mb: 6 }}>
      <BackBtn />
      <PageTitle mb={3}>Campaign API Documentation</PageTitle>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Overview
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The Campaign API allows you to send WhatsApp campaign messages using a
          predefined template. This endpoint supports sending messages to
          multiple phone numbers with optional media and parameterized content.
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Endpoint Details
        </Typography>
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>URL</TableCell>
                <TableCell>
                  <code>{apiCred?.endpoint?.campaign}</code>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Method</TableCell>
                <TableCell>POST</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Authentication
                </TableCell>
                <TableCell>
                  Bearer Token (Include in Authorization header)
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Request Body
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The request body must be sent as JSON with the following parameters:
        </Typography>
        <Paper elevation={2} sx={{ p: 2, bgcolor: "#1e1e1e" }}>
          <SyntaxHighlighter
            language="json"
            style={materialDark}
            showLineNumbers
          >
            {requestBody}
          </SyntaxHighlighter>
        </Paper>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Parameters
        </Typography>
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Parameter</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Required</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parameters.map((param) => (
                <TableRow key={param.name}>
                  <TableCell>{param.name}</TableCell>
                  <TableCell>{param.type}</TableCell>
                  <TableCell>{param.required ? "Yes" : "No"}</TableCell>
                  <TableCell>{param.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Example Request
        </Typography>
        <Paper elevation={2} sx={{ p: 2, bgcolor: "#1e1e1e" }}>
          <SyntaxHighlighter
            language="bash"
            style={materialDark}
            showLineNumbers
          >
            {`curl -X POST ${apiCred?.endpoint?.campaign} \\
  -H "Authorization: Bearer YOUR_TOKEN_HERE" \\
  -H "Content-Type: application/json" \\
  -d '${requestBody}'`}
          </SyntaxHighlighter>
        </Paper>
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom>
          Notes
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          component="ul"
          sx={{ pl: 2 }}
        >
          <li>
            Ensure the Bearer token is valid and included in the Authorization
            header.
          </li>
          <li>Phone numbers must be in E.164 format (e.g., +1234567890).</li>
          <li>
            The <code>mediaUrl</code> must be a publicly accessible URL if
            provided.
          </li>
          <li>
            Template parameters (<code>bodyParams</code>,{" "}
            <code>headerParams</code>) must match the template's placeholder
            count and order.
          </li>
        </Typography>
      </Box>
    </Container>
  );
};

export default CampaignApiDoc;
